import { useCallback, useRef, useState } from 'react';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';
import {
  ButtonIcon,
  Icon,
  Menu,
  type MenuOption,
} from '@saviynt/design-system';
import { useIntl } from 'react-intl';

import { useUpdatePolicy } from '../../hooks/useAgentPolicy';
import { DeletePolicyModal } from '../DeletePolicyModal/DeletePolicyModal';
import { getKebabMenuList } from '../PolicyPageTable/PolicyTableColumnConfig';

interface ActionButtonProps {
  rowData: AgentPolicy;
  onPolicyUpdate: (data: Partial<AgentPolicy>) => void;
  onPolicyDeleted?: (policyId: string) => void;
  onStatusChange?: (message: string) => void;
}

export default function ActionButton({
  rowData,
  onPolicyUpdate,
  onPolicyDeleted,
  onStatusChange,
}: ActionButtonProps) {
  const intl = useIntl();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const pendingIsActiveRef = useRef<boolean | null>(null);
  const kebabMenuOptions = getKebabMenuList(intl).filter(
    (option) =>
      !(option.value === 'deActivate' && !rowData.isActive) &&
      !(option.value === 'activate' && rowData.isActive)
  );
  const { id: policyId } = rowData;

  const updatePolicyMutation = useUpdatePolicy({
    onError: () => {},
    onSuccess: (payload) => {
      onPolicyUpdate(payload);
      if (pendingIsActiveRef.current !== null) {
        const message = pendingIsActiveRef.current
          ? intl.formatMessage({
              id: '6+IOVH',
              defaultMessage: 'Rule activated successfully.',
              description: 'Success banner on policy activation',
            })
          : intl.formatMessage({
              id: 'Q6vL8h',
              defaultMessage: 'Rule deactivated successfully.',
              description: 'Success banner on policy deactivation',
            });
        onStatusChange?.(message);
        pendingIsActiveRef.current = null;
      }
    },
  });

  const handleMenuClick = useCallback(
    (selectedOption: MenuOption) => {
      switch (selectedOption.value) {
        case 'activate':
        case 'deActivate':
          pendingIsActiveRef.current = selectedOption.value === 'activate';
          updatePolicyMutation.mutate({
            policyId,
            payload: {
              isActive: selectedOption.value === 'activate',
            },
          });
          break;
        case 'delete':
          setIsDeleteModalOpen(true);
          break;
      }
    },
    [policyId, updatePolicyMutation]
  );

  return (
    <>
      <DeletePolicyModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        policyId={policyId}
        policyName={rowData.name}
        onDeleteSuccess={() => onPolicyDeleted?.(policyId)}
      />
      <Menu
        isPortalEnabled
        options={kebabMenuOptions}
        onChange={handleMenuClick}
        trigger={
          <ButtonIcon
            size='medium'
            kind='ghost'
            icon={
              <Icon kind='MoreVertical' color='neutral-100' size='medium' />
            }
            ariaLabel={intl.formatMessage(
              {
                defaultMessage: 'Actions for {policyName}',
                description:
                  'Accessibility label for action button with policy name',
                id: 'wIWVk5',
              },
              {
                policyName: rowData.name,
              }
            )}
          />
        }
        dataTestId='ispm-identities-kebabMenu'
      />
    </>
  );
}
